import React, { FC, useCallback, useEffect, useState } from "react";
import { GetWebsiteAnalysisQuery, useAnalyzeWebsiteMutation, useGetWebsiteAnalysisQuery } from "generated/graphql";
import { useBoolState } from "utils/bool";
import { useWrapUrql } from "utils/graphql/useWrapUrql";

import { AnalyzeWebsiteStep, AnalyzeWebsiteStepProps } from "../components/AnalyzeWebsiteStep";

export interface AnalyzeWebsiteStepContainerProps extends Omit<AnalyzeWebsiteStepProps, "analyzing" | "onAnalyze" | "onSkipAnalyze"> {
    onNextStep(website: string, siteData?: GetWebsiteAnalysisQuery): void;
}

export const AnalyzeWebsiteStepContainer: FC<AnalyzeWebsiteStepContainerProps> = props => {

    const { onNextStep } = props;

    const [analyzing, doneAnalyzing, startAnalyzing] = useBoolState(false);
    const [executionId, setExecutionId] = useState<string>();
    const [siteData, setSiteData] = useState<GetWebsiteAnalysisQuery>();

    const analyzeSite = useWrapUrql(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: "Analysis Started"
    });

    const [getAnalysis, executeGetAnalysis] = useGetWebsiteAnalysisQuery({
        pause: !executionId,
        requestPolicy: "cache-and-network",
        variables: {
            executionId
        }
    });

    const { data: analysisData } = getAnalysis;

    useEffect(() => {
        if (analysisData?.onboarding?.webDetailsExecution?.completed && executionId) {
            // clear out executionId
            // the executionId ends up acting as a type of flag
            setExecutionId(undefined);
            // stop loading screen
            doneAnalyzing();
            // let know we are ready for next step
            // set the analysis
            setSiteData(analysisData);
            const website = analysisData?.onboarding?.webDetailsExecution?.siteCrawled;
            // increment steps
            onNextStep(website, analysisData);
        }
    }, [analysisData, doneAnalyzing, executionId, onNextStep])

    useEffect(() => {
        // this polls the executeGetAnalysis query until it is done.
        if (executionId && !getAnalysis.fetching && !siteData) {
            const id = setTimeout(() => executeGetAnalysis({ requestPolicy: 'network-only' }), 2000);
            return () => clearTimeout(id);
        }
    }, [executionId, getAnalysis.fetching, executeGetAnalysis, siteData]);

    const handleAnalyze = useCallback(async (website: string) => {
        startAnalyzing();

        await analyzeSite({ url: website }).then((result) => {
            // do we keep track of the executionId somewhere incase of a refresh?
            setExecutionId(result?.data?.onboarding?.startRetrieveWebsiteDetails?.executionId);
        }).catch((e) => {
            console.log(e);
            setExecutionId(undefined);
            doneAnalyzing();
        });
        // Now we query to see when it is done!
    }, [analyzeSite, doneAnalyzing, startAnalyzing]);

    const handleSkipAnalyze = useCallback((website: string) => {
        setExecutionId(undefined);
        doneAnalyzing();
        onNextStep(website);
    }, [doneAnalyzing, onNextStep])

    return (
        <AnalyzeWebsiteStep
            {...props}
            analyzing={analyzing}
            onAnalyze={handleAnalyze}
            onSkipAnalyze={handleSkipAnalyze}
        />
    );
}